import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Users, CreditCard, Shield, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Terms = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 hover:scale-105 transition-all duration-300 cursor-pointer group">
                          <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10 -translate-y-0.5"
              />
            <span className="text-2xl font-bold text-gray-900">
              Kauf<span className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-700 bg-clip-text text-transparent group-hover:from-emerald-400 group-hover:via-emerald-500 group-hover:to-emerald-600 transition-all duration-300">Pause</span>
            </span>
          </div>
          <button 
            onClick={() => navigate('/')} 
            className="text-gray-600 hover:text-emerald-600 transition-colors"
          >
            ← Zurück zur Startseite
          </button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-16 mt-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <FileText className="w-8 h-8 text-emerald-600" />
              <h1 className="text-4xl font-bold text-gray-900">Allgemeine Geschäftsbedingungen</h1>
            </div>
            <p className="text-xl text-gray-600">
              Nutzungsbedingungen für KaufPause
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Stand: {new Date().toLocaleDateString('de-DE')}
            </p>
          </div>

          <div className="space-y-8">
            <Card className="border-emerald-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <FileText className="w-5 h-5" />
                  1. Geltungsbereich
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung der KaufPause-App 
                  und aller damit verbundenen Dienste. Durch die Registrierung und Nutzung unserer App 
                  erklären Sie sich mit diesen Bedingungen einverstanden.
                </p>
                <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-600">
                  <p className="text-emerald-800 font-medium">
                    <strong>Wichtig:</strong> Bitte lesen Sie diese AGB sorgfältig durch, bevor Sie 
                    KaufPause nutzen.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Users className="w-5 h-5" />
                  2. Nutzerkonto und Registrierung
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Registrierung:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Sie müssen mindestens 16 Jahre alt sein</li>
                      <li>• Eine gültige E-Mail-Adresse ist erforderlich</li>
                      <li>• Sie sind für die Sicherheit Ihres Passworts verantwortlich</li>
                      <li>• Pro Person ist nur ein Account erlaubt</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Nutzerpflichten:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Wahrheitsgemäße Angaben bei der Registrierung</li>
                      <li>• Keine missbräuchliche Nutzung der App</li>
                      <li>• Respektvoller Umgang mit anderen Nutzern</li>
                      <li>• Einhaltung geltender Gesetze</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <CreditCard className="w-5 h-5" />
                  3. Preise und Zahlungsbedingungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Kostenlose Version:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• 1 aktives Produkt</li>
                      <li>• Alle Grundfunktionen</li>
                      <li>• Unbegrenzte Nutzungsdauer</li>
                      <li>• Keine versteckten Kosten</li>
                    </ul>
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mt-3">
                      <p className="text-yellow-800 text-xs">
                        <strong>Hinweis:</strong> Wir behalten uns vor, die Funktionen des kostenlosen Kontos 
                        jederzeit ohne vorherige Ankündigung anzupassen, zu erweitern oder zu reduzieren. 
                        Ebenso können wir den kostenlosen Service jederzeit einstellen.
                      </p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Premium Version:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• 16,90€ pro Jahr (Einmalzahlung)</li>
                      <li>• Unbegrenzte Produkte</li>
                      <li>• Erweiterte Features</li>
                      <li>• Keine automatische Verlängerung</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-purple-800 text-sm">
                    <strong>Zahlungsabwicklung:</strong> Alle Zahlungen werden sicher über PayPal abgewickelt. 
                    Preise verstehen sich inklusive der gesetzlichen Mehrwertsteuer. Premium-Lizenzen sind 
                    Einmalzahlungen ohne automatische Verlängerung.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-800">
                  <Shield className="w-5 h-5" />
                  4. Kündigung und Widerruf
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Kündigung durch Sie:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Account jederzeit ohne Angabe von Gründen löschbar</li>
                      <li>• Löschung über die App-Einstellungen oder per E-Mail</li>
                      <li>• Premium-Lizenzen laufen nach einem Jahr automatisch aus</li>
                      <li>• Keine automatische Verlängerung oder versteckte Kosten</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Widerrufsrecht:</h4>
                    <p className="text-gray-700 text-sm">
                      Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag 
                      zu widerrufen. Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.
                    </p>
                  </div>
                  
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mt-3">
                    <h4 className="font-semibold text-amber-900 mb-1">Keine Rückerstattung bei Kontolöschung:</h4>
                    <p className="text-amber-800 text-sm">
                      Bei Löschung Ihres Kontos besteht kein Anspruch auf Rückerstattung bereits gezahlter 
                      Beiträge. Dies gilt auch für anteilige Rückerstattungen bei noch nicht abgelaufenen 
                      Premium-Lizenzen. Mit der Kontolöschung erlöschen sofort alle Premium-Vorteile und 
                      Zugriffsrechte auf die Plattform.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-800">
                  <AlertTriangle className="w-5 h-5" />
                  5. Haftung und Gewährleistung
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Verfügbarkeit:</h4>
                    <p className="text-gray-700 text-sm">
                      Wir bemühen uns um eine hohe Verfügbarkeit der App, können jedoch keine 100%ige 
                      Verfügbarkeit garantieren. Wartungsarbeiten werden nach Möglichkeit angekündigt.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Haftungsbeschränkung:</h4>
                    <p className="text-gray-700 text-sm">
                      Unsere Haftung ist auf Vorsatz und grobe Fahrlässigkeit beschränkt. Für leichte 
                      Fahrlässigkeit haften wir nur bei Verletzung wesentlicher Vertragspflichten.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Datenverlust:</h4>
                    <p className="text-gray-700 text-sm">
                      Wir erstellen regelmäßige Backups Ihrer Daten. Dennoch empfehlen wir Ihnen, 
                      wichtige Daten zusätzlich zu sichern.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">6. Schlussbestimmungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Änderungen der AGB:</h4>
                    <p className="text-gray-700 text-sm">
                      Wir behalten uns vor, diese AGB zu ändern. Über wesentliche Änderungen werden 
                      Sie per E-Mail informiert. Widersprechen Sie nicht innerhalb von 30 Tagen, 
                      gelten die neuen AGB als angenommen.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Anwendbares Recht:</h4>
                    <p className="text-gray-700 text-sm">
                      Es gilt österreichisches Recht unter Ausschluss des UN-Kaufrechts. Gerichtsstand ist 
                      Wien, sofern Sie Unternehmer sind.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Salvatorische Klausel:</h4>
                    <p className="text-gray-700 text-sm">
                      Sollten einzelne Bestimmungen dieser AGB unwirksam sein, bleibt die Wirksamkeit 
                      der übrigen Bestimmungen unberührt.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 text-sm">
                    <strong>Kontakt bei Fragen:</strong> <a href="mailto:kontakt@kaufpause.at" className="text-emerald-600 hover:underline">kontakt@kaufpause.at</a><br/>
                    <strong>Letzte Aktualisierung:</strong> {new Date().toLocaleDateString('de-DE')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms; 